import React, {memo, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '~/redux/root-store';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {Col, UiImage, UiText, UiQuestionnaireLayout, UiBox, UiSimpleGrid, UiButton} from '~/components/elements';
import QuestionForm from '~/assets/images/icons/question-form.png';
import {RootState} from '~/redux/root-reducer';
import {METHOD_KEYS, STI_TELECONSULTATION_KEYS} from '~/constants/medical-questionnaires';
import {setNextQuestionnaire, setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {Option} from '~/redux/questionnaires/types';
import {spacing} from '~/utils/theme/spacing';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {requestOK} from '~/utils/thunk-api';
import {unwrapResult} from '@reduxjs/toolkit';
import {useHistory} from 'react-router-dom';
import {getCurrentUserFromStorage} from '~/services/auth';

const Questionnaire10Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const {addMessage} = useNotification(dispatch);

  const {loading} = useRequestState(addQuestionnaire);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[STI_TELECONSULTATION_KEYS.STATEMENT_BEST_APPLIES]?.data,
  );

  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);

  const NONE_VALUE = 5;

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const {wpAuthUser} = medicalQuestionnaire;

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === STI_TELECONSULTATION_KEYS.STATEMENT_BEST_APPLIES);

  let medicalOptions: Option[] = [];
  if (questionnaire?.options?.length) {
    medicalOptions = questionnaire?.options?.filter(i => i?.value !== NONE_VALUE);
  }

  const defaultOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    if (currentAnswer?.selectedOption?.length) {
      currentAnswer?.selectedOption.map((i: string | number) => {
        if (i !== NONE_VALUE) res[i] = true;
      });
    }

    return res;
  }, {});

  const resetOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    return res;
  }, {});

  const {inputs, setInputs, validation} = useInput({
    defaultOptions,
    noneOptions: (currentAnswer?.selectedOption?.length && currentAnswer?.selectedOption[0] === NONE_VALUE) || false,
  });
  const dataSubmit = Object.keys(inputs.defaultOptions).filter(i => inputs.defaultOptions[i] === true);

  const onClickOption = (answer: {value: string | number; label: string}) => {
    const {value} = answer;
    setInputs({noneOptions: false, defaultOptions: {...inputs.defaultOptions, [value]: !inputs.defaultOptions[value]}});
  };

  const onClickNoneOption = () => {
    setInputs({noneOptions: !inputs.noneOptions, defaultOptions: resetOptions});
  };

  const onClickSubmit = async () => {
    const questionnaireAnswer = {
      key: STI_TELECONSULTATION_KEYS.STATEMENT_BEST_APPLIES,
      index: questionnaire?.index || '',
      data: {selectedOption: inputs?.noneOptions ? [NONE_VALUE] : dataSubmit},
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));

    if (inputs.noneOptions) {
      dispatch(setNextQuestionnaire({value: STI_TELECONSULTATION_KEYS.MORE_INFORMATION}));
    } else {
      const resAction = await dispatch(
        addQuestionnaire({
          data: {
            data: {
              ...medicalQuestionnaireAnswer,
              [STI_TELECONSULTATION_KEYS.STATEMENT_BEST_APPLIES]: {...questionnaireAnswer},
            },
            wpUserId: wpAuthUser?.wpUserId,
            methodKey: METHOD_KEYS.STI_TELECONSULTATION,
          },
        }),
      );

      if (requestOK(resAction, addQuestionnaire)) {
        const result = unwrapResult(resAction);
        if (result?.booking) {
          const type = result?.slotTime;
          dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
          history.push(`/appointment?type=${type}&methodKey=${METHOD_KEYS.STI_TELECONSULTATION}&token=${savedToken}`);
        }
      }
    }
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!dataSubmit.length && !inputs.noneOptions}
      handleOnNext={onClickSubmit}
      loading={loading}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={{base: 1, lg: 2}} spacing={5}>
            {medicalOptions?.map(value => (
              <UiButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.defaultOptions[value.value] ? 'solid' : 'toggle'}
                onClick={() => onClickOption(value)}
                height={spacing.standard}
                px={5}
              />
            ))}
          </UiSimpleGrid>
          <UiBox mt={spacing.small} display="flex" alignItems="center" justifyContent="center" w="100%">
            <UiButton
              w="100%"
              px={spacing.medium}
              title="None of the above"
              variant={inputs.noneOptions ? 'solid' : 'toggle'}
              height={spacing.standard}
              onClick={onClickNoneOption}
            />
          </UiBox>
        </UiBox>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire10Screen;
